import type {
  BackgroundCheckState,
  CurrentStatusState,
  DayBucket,
  OnboardingStepStatus,
  WorkerListItem,
} from '@/services/workersV2API';
import type { Option } from './mappings';

// GRAVY-10975: the Onboarding, Background check and I-9 tabs are column
// views over the same list (the design shows the same workers on each); the
// Status filter narrows it. The All tab keeps the GRAVY-10436 page as it was.
export type ColumnTab = 'onboarding' | 'background_check' | 'i9';

export const RANGE_OPTIONS = [30, 90, 365] as const;
export type RangeDays = (typeof RANGE_OPTIONS)[number];
export const DEFAULT_RANGE: RangeDays = 30;

export const CURRENT_STATUS_OPTIONS: Option<CurrentStatusState>[] = [
  { label: 'Onboarding', value: 'onboarding' },
  { label: 'BG check pending', value: 'bg_check_pending' },
  { label: 'I-9 pending', value: 'i9_pending' },
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
];

// Same buckets as the API's `progress` param (days on the current pending
// status, Workers::CurrentStatus) — the number the Stuck for pill shows.
export const DAYS_STUCK_OPTIONS: Option<DayBucket>[] = [
  { label: '0-7 days', value: 'up_to_7_days' },
  { label: '8-14 days', value: '8_to_14_days' },
  { label: '15+ days', value: '15_plus_days' },
];

// The design draws 2 and 3 days amber, 7 and 12 red; the ticket asks for 7+
// red (open question Q7 on where amber starts). Amber below 7 until product
// answers.
export const STUCK_RED_FROM_DAYS = 7;
export type StuckTone = 'amber' | 'red';
export const stuckTone = (days: number): StuckTone =>
  days >= STUCK_RED_FROM_DAYS ? 'red' : 'amber';

export const formatDays = (days: number): string =>
  `${days} ${days === 1 ? 'day' : 'days'}`;

export type SegmentTone = 'done' | 'current' | 'rejected' | 'todo';

export const stepSegmentTone = (status: OnboardingStepStatus): SegmentTone => {
  switch (status) {
    case 'completed':
      return 'done';
    case 'current':
      return 'current';
    case 'rejected':
      return 'rejected';
    // A step that does not apply (a 1099 worker's I-9, a waived interview)
    // is behind the worker, as the panel's timeline shows it with a done dot
    // and "Not required"; grey would read as a step still to do (R3D-03).
    case 'not_applicable':
      return 'done';
    default:
      return 'todo';
  }
};

// A vendor status as the BG check / I-9 tabs draw it: label, segment bar and
// an optional red warning line under it.
export type VendorStatus = {
  label: string;
  segments: SegmentTone[];
  warning: string | null;
  // Red status text (expired / suspended), never colour alone: the label
  // always carries the state.
  alert: boolean;
};

const bar = (total: number, ...tones: SegmentTone[]): SegmentTone[] => [
  ...tones,
  ...Array<SegmentTone>(Math.max(0, total - tones.length)).fill('todo'),
];

// Background check: invited -> report running -> cleared (3 segments).
// The segment mapping per status still needs design sign-off (ticket Q5).
export const BG_STATUS: Record<BackgroundCheckState, VendorStatus> = {
  not_started: {
    label: 'Not started',
    segments: bar(3),
    warning: null,
    alert: false,
  },
  invitation_sent: {
    label: 'Invitation sent',
    segments: bar(3, 'done'),
    warning: null,
    alert: false,
  },
  invitation_expired: {
    label: 'Invitation expired',
    segments: bar(3, 'rejected'),
    warning: null,
    alert: true,
  },
  pending: {
    label: 'Pending',
    segments: bar(3, 'done', 'current'),
    warning: null,
    alert: false,
  },
  in_review: {
    label: 'In review',
    segments: bar(3, 'done', 'current'),
    warning: null,
    alert: false,
  },
  suspended: {
    label: 'Suspended',
    segments: bar(3, 'done', 'rejected'),
    warning: null,
    alert: true,
  },
  clear: {
    label: 'Clear / Eligible',
    segments: bar(3, 'done', 'done', 'done'),
    warning: null,
    alert: false,
  },
};

export const bgStatusFor = (w: WorkerListItem): VendorStatus =>
  BG_STATUS[w.background_check?.status ?? 'not_started'] ??
  BG_STATUS.not_started;

const I9_CLEAR: VendorStatus = {
  label: 'Clear / Eligible',
  segments: bar(4, 'done', 'done', 'done', 'done'),
  warning: null,
  alert: false,
};

// The stored values the switch below prints itself, so a settled I-9 does
// not override them. tentative_nc is here so its mismatch is never hidden
// behind "Clear / Eligible" (GRAVY-10988).
const I9_DONE_VALUES = ['approved', 'n/a', 'prospect', 'tentative_nc'];

// I-9 (Zeal + E-Verify): invited -> Section 1 -> Section 2 / E-Verify ->
// eligible (4 segments). Built from workers.i9_status (I9Status in graphy).
export const i9StatusFor = (w: WorkerListItem): VendorStatus | null => {
  if ((w.tax_type ?? '').toLowerCase() === '1099') return null;
  const raw = (w.statuses?.i9_status ?? '').toLowerCase();
  // GRAVY-10975 BH-21: graphy's clearance.i9 (OnboardingProgress.i9_settled?)
  // also counts an onboarded worker's ready-to-work latch as a settled I-9,
  // unless the stored value is one the tab draws red. Step 4 of the bar, the
  // I-9 icon and the I-9 status sort (FilterService i9_status_rank) all follow
  // it, so reading only the raw column printed "Not started" or "In review"
  // beside a bar saying "Verified", in the rank sorted as "Clear".
  if (w.clearance?.i9 && !I9_DONE_VALUES.includes(raw)) return I9_CLEAR;
  switch (raw) {
    case 'approved':
    case 'n/a':
    case 'prospect':
      return {
        label: 'Clear / Eligible',
        segments: bar(4, 'done', 'done', 'done', 'done'),
        warning: null,
        alert: false,
      };
    // `pending` is the I-9 state machine's initial state (Worker#i9_status):
    // every W-2 worker starts there and leaves it when they open the form
    // themselves, so nothing has been sent. It is "Not started" (the
    // default below), not "Invitation sent" with a Resend invite (review
    // R3D-01).
    case 'cancelled':
      return {
        label: 'Invitation expired',
        segments: bar(4, 'rejected'),
        warning: null,
        alert: true,
      };
    case 'in_progress':
    case 'pending_section2':
      return {
        label: 'Pending',
        segments: bar(4, 'done', 'current'),
        warning: null,
        alert: false,
      };
    // E-Verify has not confirmed the worker yet: a tentative non-confirmation
    // is an open mismatch the worker must resolve. graphy still counts it as
    // I-9 done for step 4 and ready to work (Worker#formi9_completed?), which
    // is Product question Q5, so only the wording changes here. Printing
    // "Clear / Eligible" beside "Info mismatch" told ops the case was closed
    // (GRAVY-10988). Same label as needs_attention, and the I-9 sort
    // (FilterService#i9_status_rank_sql) groups the two together.
    case 'tentative_nc':
    // A Zeal "needs attention" is the design's "Info mismatch" warning.
    case 'needs_attention':
      return {
        label: 'Pending',
        segments: bar(4, 'done', 'done', 'rejected'),
        warning: 'Info mismatch',
        alert: false,
      };
    case 'pending_e-verify':
    case 'e-verify_in_progress':
      return {
        label: 'In review',
        segments: bar(4, 'done', 'done', 'current'),
        warning: null,
        alert: false,
      };
    // `closed` is an E-Verify final non-confirmation too (Formi9Service
    // deactivates the worker for both), not an expired invitation (R3D-02).
    case 'rejected':
    case 'closed':
      return {
        label: 'Rejected',
        segments: bar(4, 'done', 'rejected'),
        warning: null,
        alert: true,
      };
    default:
      return {
        label: 'Not started',
        segments: bar(4),
        warning: null,
        alert: false,
      };
  }
};

// "Resend invite" is the design's action while an invitation is out.
export const bgNeedsInvite = (w: WorkerListItem): boolean =>
  ['invitation_sent', 'invitation_expired'].includes(
    w.background_check?.status ?? '',
  );

export const i9NeedsInvite = (w: WorkerListItem): boolean =>
  ['Invitation sent', 'Invitation expired'].includes(
    i9StatusFor(w)?.label ?? '',
  );

const MONTH_DAY: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
};

export const formatMonthDay = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', MONTH_DAY) : '';

export const formatLongDate = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        ...MONTH_DAY,
        year: 'numeric',
      })
    : '';

export const formatMonthDayTime = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        ...MONTH_DAY,
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

// Whole days since `iso`, for "Day N" on an Outreach message and "N days ago".
export const daysBetween = (fromIso: string, to: Date | string): number => {
  const from = new Date(fromIso).getTime();
  const end = typeof to === 'string' ? new Date(to).getTime() : to.getTime();
  return Math.max(0, Math.floor((end - from) / (24 * 60 * 60 * 1000)));
};
